import { css } from 'aphrodite';
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
	IMetaVisibleAsset,
	META_AD_ACCOUNT_STATUS_LABELS,
	useMetaVisibleAssetsQuery,
} from '../../../../queries/Admin/Meta';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { Pill } from '../../../../web/components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { MetaContactCardIcon } from '../icons';
import { MetaAssignModal } from '../MetaAssignModal';
import { tonePillColor } from '../utils';
import { styleSheet } from './styles';

const FILTER_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'unmapped', label: 'Unmapped only' },
	{ value: 'managed', label: 'Managed only' },
	{ value: 'customerOAuth', label: 'Customer-OAuth only' },
	{ value: 'inactive', label: 'Inactive status' },
] as const;

type FilterValue = (typeof FILTER_OPTIONS)[number]['value'];

interface IProps {
	/** Open the per-customer connection modal for an assigned row. */
	onEditConnection: (levitateAccountId: string, levitateAccountName: string | null) => void;
}

export const MetaVisibleAssetsTable: React.FC<IProps> = ({ onEditConnection }) => {
	const { data: assets, isPending } = useMetaVisibleAssetsQuery();
	const [search, setSearch] = React.useState('');
	const [filter, setFilter] = React.useState<FilterValue>('all');

	// Currently-open single-account assign target. `null` = no modal open.
	const [assignTarget, setAssignTarget] = React.useState<string | null>(null);

	const rows = React.useMemo(() => filterAndSort(assets ?? [], search, filter), [assets, search, filter]);

	const closeAssign = () => setAssignTarget(null);

	return (
		<section className={css(styleSheet.container)} aria-label='Visible Meta ad accounts'>
			<div className={css(styleSheet.headerRow)}>
				<div className={css(styleSheet.titleRow)}>
					<MetaContactCardIcon className={css(styleSheet.titleIcon)} />
					<h2 className={css(styleSheet.title)}>Visible Meta ad accounts</h2>
				</div>
			</div>
			<div className={css(styleSheet.controlsRow)}>
				<div className={css(styleSheet.searchInput)}>
					<Input
						type='search'
						placeholder='Search by ad account name or id'
						value={search}
						onChange={e => setSearch(e.target.value)}
					/>
				</div>
				<div className={css(styleSheet.filterGroup)}>
					<span>Filter</span>
					<Select value={filter} onValueChange={value => setFilter(value as FilterValue)}>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{FILTER_OPTIONS.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className={css(styleSheet.tableWrap)}>
				<table className={css(styleSheet.table)}>
					<thead>
						<tr>
							<th className={css(styleSheet.th)}>Ad Account</th>
							<th className={css(styleSheet.th)}>Business</th>
							<th className={css(styleSheet.th)}>Type</th>
							<th className={css(styleSheet.th)}>Assigned to</th>
							<th className={css(styleSheet.th)}>Status</th>
						</tr>
					</thead>
					<tbody>
						{isPending ? (
							<SkeletonRows />
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={5} className={css(styleSheet.emptyState)}>
									{search || filter !== 'all'
										? 'No ad accounts match the current filter.'
										: "Levitate's Business Manager has no visible ad accounts. Either no partner grants have been accepted yet, or the System User token isn't scoped to this Business Manager."}
								</td>
							</tr>
						) : (
							rows.map(row => (
								<MetaVisibleAssetRow
									key={row.metaAdAccountId}
									row={row}
									onAssign={() => setAssignTarget(row.metaAdAccountId)}
									onEditConnection={onEditConnection}
								/>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className={css(styleSheet.footer)}>
				<span className={css(styleSheet.resultsCount)}>
					Showing {rows.length} of {assets?.length ?? 0}
				</span>
			</div>

			{assignTarget !== null ? (
				<MetaAssignModal
					metaAdAccountId={assignTarget}
					visibleAssets={assets ?? []}
					onClose={closeAssign}
					onSuccess={closeAssign}
				/>
			) : null}
		</section>
	);
};

const MetaVisibleAssetRow: React.FC<{
	row: IMetaVisibleAsset;
	onAssign: () => void;
	onEditConnection: (levitateAccountId: string, levitateAccountName: string | null) => void;
}> = ({ row, onAssign, onEditConnection }) => {
	const status = META_AD_ACCOUNT_STATUS_LABELS[row.accountStatus] ?? {
		label: `Status ${row.accountStatus}`,
		tone: 'neutral' as const,
	};

	return (
		<tr>
			<td className={css(styleSheet.td)}>
				<div className={css(styleSheet.nameCell)}>
					<span className={css(styleSheet.accountName)}>{row.name}</span>
					<span className={css(styleSheet.accountId)}>id: {row.metaAdAccountId}</span>
				</div>
			</td>
			<td className={css(styleSheet.td)}>{row.businessName || '—'}</td>
			<td className={css(styleSheet.td)}>{row.isClientAccount ? 'Client' : 'Owned'}</td>
			<td className={css(styleSheet.td)}>
				<AssignmentCell row={row} onAssign={onAssign} onEditConnection={onEditConnection} />
			</td>
			<td className={css(styleSheet.td)}>
				<Pill variant='label' color={tonePillColor(status.tone)} label={status.label} />
			</td>
		</tr>
	);
};

const AssignmentCell: React.FC<{
	row: IMetaVisibleAsset;
	onAssign: () => void;
	onEditConnection: (levitateAccountId: string, levitateAccountName: string | null) => void;
}> = ({ row, onAssign, onEditConnection }) => {
	const managed = row.managedByLevitateAccounts ?? [];
	const owned = row.ownedByLevitateAccounts ?? [];

	// Fall back to the unassigned affordance if the row claims to be mapped but
	// carries no resolvable Levitate accounts in either array — without this the
	// cell would render an empty <div> with no Edit/Assign action.
	if (row.unmapped || (managed.length === 0 && owned.length === 0)) {
		return (
			<div className={css(styleSheet.assignedCell)}>
				<span className={css(styleSheet.unassigned)}>Unassigned</span>
				<Button variant='link' onClick={onAssign} styles={[styleSheet.editButton]}>
					Assign Account
				</Button>
			</div>
		);
	}

	const managedSet = new Set(managed.map(a => a.id));
	const ownedSet = new Set(owned.map(a => a.id));
	// De-dupe by id while preserving the inline name from whichever array first carries it.
	const nameById = new Map<string, string>();
	[...managed, ...owned].forEach(a => {
		if (!nameById.has(a.id)) nameById.set(a.id, a.name || `Account ${a.id}`);
	});
	const lines: { levitateAccountId: string; name: string; role: 'managed' | 'customer' | 'both' }[] = [];
	nameById.forEach((name, id) => {
		const role: 'managed' | 'customer' | 'both' =
			managedSet.has(id) && ownedSet.has(id) ? 'both' : managedSet.has(id) ? 'managed' : 'customer';
		lines.push({ levitateAccountId: id, name, role });
	});

	return (
		<div className={css(styleSheet.assignedCell)}>
			{lines.map(line => {
				// Render a per-line block with its own Manage link so multi-assigned ad
				// accounts let the operator open any of their connection modals (not just lines[0]).
				const renderLine = (suffix: 'managed' | 'customer') => (
					<div className={css(styleSheet.assignedLine)} key={`${line.levitateAccountId}-${suffix}`}>
						<Link to={`/accounts/${line.levitateAccountId}/ads`} className={css(styleSheet.assignedAccountLink)}>
							{line.name}
						</Link>
						<span className={css(styleSheet.assignedRole)}>({suffix})</span>
					</div>
				);
				const ariaLabel = `Manage Meta connection for ${line.name}`;
				return (
					<div className={css(styleSheet.assignedAccountGroup)} key={line.levitateAccountId}>
						{line.role === 'both' ? (
							<>
								{renderLine('managed')}
								{renderLine('customer')}
							</>
						) : (
							renderLine(line.role)
						)}
						<Button
							variant='link'
							onClick={() => onEditConnection(line.levitateAccountId, line.name)}
							styles={[styleSheet.editButton]}
							aria-label={ariaLabel}
						>
							Manage
						</Button>
					</div>
				);
			})}
		</div>
	);
};

const SkeletonRows: React.FC = () => (
	<>
		{Array.from({ length: 6 }).map((_, i) => (
			<tr key={i}>
				<td colSpan={5} className={css(styleSheet.skeletonRow)} aria-busy='true' />
			</tr>
		))}
	</>
);

const filterAndSort = (assets: IMetaVisibleAsset[], search: string, filter: FilterValue): IMetaVisibleAsset[] => {
	const lowerSearch = search.trim().toLowerCase();
	const filtered = assets.filter(asset => {
		if (lowerSearch) {
			const match =
				asset.name.toLowerCase().includes(lowerSearch) || asset.metaAdAccountId.toLowerCase().includes(lowerSearch);
			if (!match) return false;
		}
		switch (filter) {
			case 'unmapped':
				return asset.unmapped;
			case 'managed':
				return (asset.managedByLevitateAccounts ?? []).length > 0;
			case 'customerOAuth':
				return (asset.ownedByLevitateAccounts ?? []).length > 0;
			case 'inactive':
				// Status 201 ("Any active") also maps to a `success` tone — treat anything
				// non-success as inactive so the filter matches the Pill the operator sees.
				return META_AD_ACCOUNT_STATUS_LABELS[asset.accountStatus]?.tone !== 'success';
			case 'all':
			default:
				return true;
		}
	});
	// Unmapped first, then by name asc.
	return filtered.sort((a, b) => {
		if (a.unmapped !== b.unmapped) return a.unmapped ? -1 : 1;
		return a.name.localeCompare(b.name);
	});
};
